import React, { useRef, useCallback, useState } from 'react';
import { Bold, Italic, List, ListOrdered, Heading2, Eraser, Link, AlignLeft, AlignCenter, AlignRight, Type, ChevronDown } from 'lucide-react';

// Toolbar button component - compact design
const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1 rounded hover:bg-gray-200 transition-colors ${active ? 'bg-gray-200 text-purple-600' : 'text-gray-600'}`}
  >
    {children}
  </button>
);

/**
 * SimpleRichTextEditor - A React 19 compatible rich text editor
 * Uses contentEditable with execCommand for basic formatting
 * Features: Font size, Bold, Italic, Lists, Headings, Links, Alignments
 */
const SimpleRichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Write your content here...",
  minHeight = "150px",
  className = "",
  compact = false // For smaller description fields
}) => {
  const editorRef = useRef(null);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const execCommand = useCallback((command, value = null) => {
    // Ensure editor has focus
    if (editorRef.current) {
      editorRef.current.focus();
      
      // Execute command on the document
      try {
        document.execCommand(command, false, value);
      } catch (error) {
        console.error('execCommand failed:', command, error);
      }
      
      // Trigger onChange after command
      if (onChange) {
        // Small delay to ensure command is applied
        setTimeout(() => {
          onChange(editorRef.current.innerHTML);
        }, 10);
      }
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      const html = editorRef.current.innerHTML;
      // Don't trigger if just placeholder
      if (html === '<br>' || html === '') {
        onChange('');
      } else {
        onChange(html);
      }
    }
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const formatHeading = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      if (selectedText) {
        execCommand('formatBlock', 'h2');
      }
    }
  }, [execCommand]);

  const setFontSize = useCallback((size) => {
    execCommand('fontSize', size);
    setShowFontSizeMenu(false);
  }, [execCommand]);

  const insertLink = useCallback(() => {
    if (linkUrl) {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      execCommand('createLink', url);
      setLinkUrl('');
      setShowLinkInput(false);
    }
  }, [linkUrl, execCommand]);

  // Set initial content
  React.useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const fontSizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '4' },
    { label: 'X-Large', value: '5' },
  ];

  return (
    <div className={`simple-rich-editor border rounded-lg overflow-visible ${className}`}>
      {/* Toolbar - compact single row */}
      <div className="flex items-center gap-0.5 p-1 bg-gray-50 border-b flex-nowrap">
        {/* Font Size Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
            className="flex items-center gap-0.5 p-1 rounded hover:bg-gray-200 text-gray-600"
            title="Font Size"
          >
            <Type className="h-3.5 w-3.5" />
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
          {showFontSizeMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[80px]">
              {fontSizes.map((size) => (
                <button
                  key={size.value}
                  type="button"
                  onClick={() => setFontSize(size.value)}
                  className="w-full text-left px-2 py-1 hover:bg-gray-100 text-xs"
                >
                  {size.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-4 bg-gray-300" />
        
        <ToolbarButton onClick={formatHeading} title="Heading">
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('bold')} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        
        <div className="w-px h-4 bg-gray-300" />
        
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullets">
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbers">
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        
        <div className="w-px h-4 bg-gray-300" />
        
        {/* Alignment buttons */}
        <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Left">
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Center">
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyRight')} title="Right">
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>
        
        <div className="w-px h-4 bg-gray-300" />
        
        {/* Link button */}
        <div className="relative">
          <ToolbarButton onClick={() => setShowLinkInput(!showLinkInput)} title="Link">
            <Link className="h-3.5 w-3.5" />
          </ToolbarButton>
          {showLinkInput && (
            <div className="absolute top-full right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 p-2 flex gap-1.5" style={{ minWidth: '220px' }}>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL"
                className="border rounded px-2 py-1 text-xs flex-1 min-w-0"
                onKeyDown={(e) => e.key === 'Enter' && insertLink()}
                autoFocus
              />
              <button
                type="button"
                onClick={insertLink}
                className="bg-indigo-600 text-white px-2 py-1 rounded text-xs hover:bg-indigo-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          )}
        </div>
        
        <div className="w-px h-4 bg-gray-300" />
        
        <ToolbarButton onClick={() => execCommand('removeFormat')} title="Clear">
          <Eraser className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>
      
      {/* Editor Area - Auto expandable, NO max-height limit */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        className="editor-content p-2 outline-none text-sm"
        style={{ 
          minHeight: compact ? '60px' : minHeight,
          maxHeight: 'none',  // Remove any height restrictions
          overflowY: 'visible',  // Changed from auto to visible - no scroll, auto-expand
          overflowX: 'hidden'  // Prevent horizontal overflow only
        }}
      />
      
      <style>{`
        .simple-rich-editor .editor-content:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .simple-rich-editor .editor-content h1,
        .simple-rich-editor .editor-content h2,
        .simple-rich-editor .editor-content h3 {
          font-weight: 700;
          margin-bottom: 0.5em;
        }
        .simple-rich-editor .editor-content h1 { font-size: 1.5em; }
        .simple-rich-editor .editor-content h2 { font-size: 1.25em; }
        .simple-rich-editor .editor-content h3 { font-size: 1.1em; }
        .simple-rich-editor .editor-content ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .simple-rich-editor .editor-content ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .simple-rich-editor .editor-content li {
          margin-bottom: 0.25em;
          display: list-item;
        }
        .simple-rich-editor .editor-content p {
          margin-bottom: 0.5em;
        }
        .simple-rich-editor .editor-content a {
          color: #4f46e5;
          text-decoration: underline;
        }
        .simple-rich-editor .editor-content a:hover {
          color: #4338ca;
        }
        /* Ensure content can expand */
        .simple-rich-editor .editor-content {
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: pre-wrap;
        }
        /* Remove any height restrictions that might be inherited */
        .simple-rich-editor {
          height: auto !important;
        }
      `}</style>
    </div>
  );
};

/**
 * Parse rich text HTML content into structured data
 * @param {string} htmlContent - HTML content from the editor
 * @returns {object} - Structured content with headline, bullets, body
 */
export const parseRichTextContent = (htmlContent) => {
  if (!htmlContent || htmlContent === '<br>' || htmlContent.trim() === '') {
    return { headline: '', bullets: [], body: '', plainText: '' };
  }

  // Create a temporary DOM element to parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  let headline = '';
  let bullets = [];
  let bodyParts = [];
  
  // Process all child elements
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text && !headline) {
        headline = text;
      } else if (text) {
        bodyParts.push(text);
      }
      return;
    }
    
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    
    const tagName = node.tagName.toLowerCase();
    const text = node.textContent.trim();
    
    if (!text) return;
    
    // Headers become headline
    if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3') {
      if (!headline) {
        headline = text;
      } else {
        bodyParts.push(text);
      }
    }
    // Lists become bullets
    else if (tagName === 'ul' || tagName === 'ol') {
      const listItems = node.querySelectorAll('li');
      listItems.forEach(li => {
        const itemText = li.textContent.trim();
        if (itemText) bullets.push(itemText);
      });
    }
    // Divs and paragraphs
    else if (tagName === 'div' || tagName === 'p') {
      // Check for bold text that could be headline
      const strong = node.querySelector('strong, b');
      if (strong && !headline && node.firstChild === strong) {
        headline = strong.textContent.trim();
        const rest = text.replace(headline, '').trim();
        if (rest) bodyParts.push(rest);
      } else if (!headline) {
        headline = text;
      } else {
        bodyParts.push(text);
      }
    }
    // Standalone bold could be headline
    else if ((tagName === 'strong' || tagName === 'b') && !headline) {
      headline = text;
    }
    else {
      // Process children
      node.childNodes.forEach(processNode);
    }
  };
  
  // Process body children
  Array.from(doc.body.childNodes).forEach(processNode);
  
  // If still no headline, use first body part
  if (!headline && bodyParts.length > 0) {
    headline = bodyParts.shift();
  }
  
  const body = bodyParts.join(' ');
  const plainText = [headline, ...bullets, body].filter(Boolean).join('\n');
  
  return {
    headline,
    bullets,
    body,
    plainText,
    hasFormatting: bullets.length > 0 || bodyParts.length > 0
  };
};

/**
 * Format content for display on creative
 */
export const formatContentForPlatform = (parsedContent, platform) => {
  const { headline, bullets, body } = parsedContent;
  
  const platformFormats = {
    'instagram-post': { maxHeadlineLength: 50, maxBullets: 4, maxBodyLength: 100 },
    'instagram-story': { maxHeadlineLength: 40, maxBullets: 3, maxBodyLength: 80 },
    'facebook-post': { maxHeadlineLength: 60, maxBullets: 5, maxBodyLength: 150 },
    'linkedin-post': { maxHeadlineLength: 70, maxBullets: 5, maxBodyLength: 200 },
    'twitter-post': { maxHeadlineLength: 50, maxBullets: 3, maxBodyLength: 100 },
    'youtube-thumbnail': { maxHeadlineLength: 40, maxBullets: 0, maxBodyLength: 0 }
  };
  
  const format = platformFormats[platform] || platformFormats['instagram-post'];
  
  return {
    headline: headline?.substring(0, format.maxHeadlineLength) || '',
    bullets: bullets?.slice(0, format.maxBullets) || [],
    body: body?.substring(0, format.maxBodyLength) || ''
  };
};

export default SimpleRichTextEditor;
